import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Lock } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { FECHA_MAXIMA, FECHA_MINIMA, fechaFueraDeRango } from "@/lib/fechas"
import { PRIORIDADES, type PrioridadTarea, TIPOS, type TipoTarea } from "./TarjetaTarea"

/**
 * El encargo: qué hay que hacer, de qué tipo, con qué urgencia y para cuándo.
 *
 * ⚠ **Esto lo edita quien reparte la tarea, no quien la ejecuta.** Lo decide el
 * servidor y lo dice en la respuesta (`puedo_editar_core`): la pantalla no conoce
 * `creado_por` ni los permisos de nadie. Una tarea cuyo título cambia por debajo deja
 * a quien la repartió persiguiendo algo que ya no es lo que pidió.
 *
 * ⚠ **Con la tarea ya arrancada cambia la forma de guardar, y a propósito.** Mientras
 * está pendiente, elegir una prioridad la guarda al instante: no hay estado
 * intermedio en un `<select>` y pedir un botón añade un paso a la operación más
 * frecuente. En cuanto alguien la tocó, todo pasa a guardarse junto y con un motivo
 * escrito — el servidor lo exige, y aquí se pide antes para no gastar un 409 en
 * enterarse. Cambiar el encargo de un trabajo ya empezado tiene consecuencias sobre
 * lo ya hecho; que cueste un renglón más es la mitad del sentido.
 *
 * ⚠ **`asignado_a` no está aquí.** Reasignar exige `tarea:asignar` y tiene su propia
 * ruta. `estado` tampoco: lo mueve quien ejecuta, desde el tablero o desde la ficha.
 */

type Editable = {
  id: number
  titulo: string
  descripcion: string | null
  tipo: TipoTarea
  prioridad: PrioridadTarea
  vence_en: string | null
}

type Campos = {
  titulo: string
  descripcion: string
  tipo: TipoTarea
  prioridad: PrioridadTarea
  vence_en: string
}

function desde(tarea: Editable): Campos {
  return {
    titulo: tarea.titulo,
    descripcion: tarea.descripcion ?? "",
    tipo: tarea.tipo,
    prioridad: tarea.prioridad,
    vence_en: tarea.vence_en ?? "",
  }
}

/** Lo que cambió de verdad, en la forma que espera el servidor. */
function diferencias(tarea: Editable, campos: Campos): Record<string, unknown> {
  const original = desde(tarea)
  const cambios: Record<string, unknown> = {}
  if (campos.titulo.trim() !== original.titulo) cambios.titulo = campos.titulo.trim()
  if (campos.descripcion.trim() !== original.descripcion) {
    // Vaciar la descripción es quitarla, no un descuido: viaja `null`.
    cambios.descripcion = campos.descripcion.trim() === "" ? null : campos.descripcion.trim()
  }
  if (campos.tipo !== original.tipo) cambios.tipo = campos.tipo
  if (campos.prioridad !== original.prioridad) cambios.prioridad = campos.prioridad
  if (campos.vence_en !== original.vence_en) {
    cambios.vence_en = campos.vence_en === "" ? null : campos.vence_en
  }
  return cambios
}

type Props = {
  tarea: Editable
  /** Lo dice el servidor en el detalle. Ver la nota de arriba. */
  puedeEditarCore: boolean
  /** La tarea ya no está pendiente: cualquier cambio exige motivo. */
  arrancada: boolean
  /** Se llama tras cada escritura para que el tablero y la ficha se relean. */
  onGuardado: () => void
}

export function EditorTarea({ tarea, puedeEditarCore, arrancada, onGuardado }: Props) {
  const clienteQuery = useQueryClient()
  const idBase = useId()

  // ⚠ Sin resincronizar con las props: quien usa este componente lo monta con
  // `key={tarea.id}`, así que saltar a otra tarea lo remonta con sus valores. Un
  // efecto que copiara las props encima pisaría lo que se está tecleando cada vez
  // que el sondeo de la ficha trae la respuesta.
  const [campos, setCampos] = useState<Campos>(() => desde(tarea))
  const [motivo, setMotivo] = useState("")

  const guardar = useMutation({
    mutationFn: (cambios: Record<string, unknown>) =>
      api.patch(`/api/v1/tareas/${tarea.id}`, cambios),
    onSuccess: () => {
      setMotivo("")
      clienteQuery.invalidateQueries({ queryKey: ["tarea", tarea.id] })
      onGuardado()
    },
  })

  const cambios = diferencias(tarea, campos)
  const sucio = Object.keys(cambios).length > 0
  const listo =
    campos.titulo.trim().length >= 3 &&
    !fechaFueraDeRango(campos.vence_en) &&
    (!arrancada || motivo.trim() !== "")
  const borde = { borderColor: "var(--border)" }

  /**
   * Mientras está pendiente, una elección discreta se guarda sola.
   *
   * ⚠ Salvo que sea imposible: la fecha se guarda al cambiarla, y un año de cinco
   * cifras saldría directo a un 422 sin que nadie hubiera pulsado nada.
   */
  const elegir = (parcial: Partial<Campos>, inmediato: Record<string, unknown>) => {
    setCampos((previos) => ({ ...previos, ...parcial }))
    const fecha = parcial.vence_en
    if (fecha !== undefined && fechaFueraDeRango(fecha)) return
    if (!arrancada) guardar.mutate(inmediato)
  }

  if (!puedeEditarCore) {
    return (
      <>
        <p className="mt-1 font-medium">{tarea.titulo}</p>
        {tarea.descripcion ? (
          <p className="mt-2 whitespace-pre-wrap text-sm" style={{ color: "var(--fg-muted)" }}>
            {tarea.descripcion}
          </p>
        ) : (
          <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
            Sin descripción.
          </p>
        )}
        <p
          className="mt-2 flex items-center gap-1 text-xs"
          style={{ color: "var(--fg-muted)" }}
          data-testid="encargo-bloqueado"
        >
          <Lock size={12} aria-hidden={true} />
          El encargo lo cambia quien lo asignó.
        </p>
      </>
    )
  }

  return (
    <div className="mt-1">
      <label className="block text-sm" htmlFor={`${idBase}-titulo`}>
        <span className="sr-only">Título</span>
        <input
          id={`${idBase}-titulo`}
          aria-label="Título"
          className="campo font-medium"
          value={campos.titulo}
          onChange={(e) => setCampos((p) => ({ ...p, titulo: e.target.value }))}
        />
      </label>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="block text-xs" htmlFor={`${idBase}-tipo`}>
          Tipo
          <select
            id={`${idBase}-tipo`}
            className="mt-1 w-full rounded-[var(--radius-control)] border p-1.5 text-sm"
            style={borde}
            value={campos.tipo}
            onChange={(e) =>
              elegir({ tipo: e.target.value as TipoTarea }, { tipo: e.target.value })
            }
          >
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs" htmlFor={`${idBase}-prioridad`}>
          Prioridad
          <select
            id={`${idBase}-prioridad`}
            className="mt-1 w-full rounded-[var(--radius-control)] border p-1.5 text-sm"
            style={borde}
            value={campos.prioridad}
            onChange={(e) =>
              elegir({ prioridad: e.target.value as PrioridadTarea }, { prioridad: e.target.value })
            }
          >
            {PRIORIDADES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-2 block text-xs" htmlFor={`${idBase}-vence`}>
        Vence el
        <input
          id={`${idBase}-vence`}
          type="date"
          min={FECHA_MINIMA}
          max={FECHA_MAXIMA}
          aria-invalid={fechaFueraDeRango(campos.vence_en)}
          className="mt-1 w-full rounded-[var(--radius-control)] border p-1.5 text-sm tabular-nums"
          style={borde}
          value={campos.vence_en}
          onChange={(e) =>
            elegir(
              { vence_en: e.target.value },
              { vence_en: e.target.value === "" ? null : e.target.value },
            )
          }
        />
      </label>

      <label className="mt-2 block text-xs" htmlFor={`${idBase}-descripcion`}>
        Descripción
        <textarea
          id={`${idBase}-descripcion`}
          rows={3}
          className="campo mt-1 text-sm"
          value={campos.descripcion}
          onChange={(e) => setCampos((p) => ({ ...p, descripcion: e.target.value }))}
        />
      </label>

      {/* El motivo aparece sólo cuando hace falta: pedirlo siempre lo convertiría en
          un campo que se rellena con «actualización» y dejaría de decir nada. */}
      {arrancada && sucio && (
        <div className="mt-2">
          {/* ⚠ La pista va FUERA del `<label>` y se enlaza con `aria-describedby`.
              Dentro, pasa a formar parte del nombre accesible del campo: un lector
              anunciaría «Motivo del cambio La tarea ya arrancó…» cada vez que se
              entra en él. */}
          <label className="block text-xs" htmlFor={`${idBase}-motivo`}>
            Motivo del cambio
          </label>
          <textarea
            id={`${idBase}-motivo`}
            aria-describedby={`${idBase}-motivo-pista`}
            rows={2}
            className="campo mt-1 text-sm"
            placeholder="El cliente movió el embarque"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <p
            id={`${idBase}-motivo-pista`}
            className="mt-1 text-xs"
            style={{ color: "var(--fg-muted)" }}
          >
            La tarea ya arrancó: el cambio queda en el historial con este motivo.
          </p>
        </div>
      )}

      {/* Aparece sólo cuando hay algo que guardar: un botón permanentemente activo
          no distingue «ya está guardado» de «se me olvidó pulsarlo». */}
      {sucio && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setCampos(desde(tarea))
              setMotivo("")
            }}
            className="boton-secundario px-2 py-1 text-xs"
          >
            Descartar
          </button>
          <button
            type="button"
            data-testid="guardar-texto"
            disabled={!listo || guardar.isPending}
            onClick={() =>
              guardar.mutate(arrancada ? { ...cambios, motivo: motivo.trim() } : cambios)
            }
            className="boton-primario px-2 py-1 text-xs disabled:opacity-50"
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}

      {guardar.isError && (
        <p role="alert" className="mt-2 text-xs" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi
            ? guardar.error.message
            : "No se pudo guardar el cambio."}
        </p>
      )}
    </div>
  )
}
